import CrossIcon from "@assets/icons/cross.svg";
import InfoIcon from "@assets/icons/info.svg";
import { useI18n } from "@core/i18n/i18n";
import { localeManager } from "@core/service-container";
import { FeaturePreview, createParagraphBlock } from "@domain/announcement/announcement";
import { sendAnalyticsOpenAlternativeExercisesInfoTooltip } from "@domain/library/exercise-analytics";
import styled from "@emotion/styled";
import { DialogContext, ModalId } from "@ui/components/dialog/dialog-provider";
import { AnimatedHalo } from "@ui/components/onboarding/animated-halo";
import { OnboardingPanel } from "@ui/components/onboarding/onboarding-panel";
import { colors } from "@ui/style/colors";
import { row } from "@ui/style/layout";
import { useObservable } from "micro-observables";
import { alternativeGuide3WasCompleted } from "@ui/pages/program-builder/quickstart/alternative-highlight-signal";
import React, { useContext, useMemo } from "react";

interface AlternativeExerciseHeaderProps {
	onCrossClicked: () => void;
}

export const AlternativeExerciseHeaderHeight = 45;

export const AlternativeExerciseHeader: React.FC<AlternativeExerciseHeaderProps> = ({ onCrossClicked }) => {
	const { format } = useI18n();
	const dialogContext = useContext(DialogContext);
	const locale = useObservable(localeManager.locale);

	// Show halo only after guide03 is completed (quickstart)
	const [showHalo, setShowHalo] = React.useState(() => Boolean(alternativeGuide3WasCompleted.get()));
	const [showGuide04Panel, setShowGuide04Panel] = React.useState(() => Boolean(alternativeGuide3WasCompleted.get()));
	React.useEffect(() => {
		const sub = alternativeGuide3WasCompleted.subscribe(val => {
			setShowHalo(Boolean(val));
			setShowGuide04Panel(Boolean(val));
		});
		return () => sub();
	}, []);

	const ALTERNATIVE_EXERCISE_FEATURE_PREVIEW: FeaturePreview = useMemo(
		() => ({
			title: format("programBuilder.alternativeExercises.infoDialog.title"),
			pages: [
				{
					imageUrl: `https://res.cloudinary.com/physio-me/image/upload/v1707823263/Physio.Me/Alternative%20Exercises%20Feature%20Preview/${locale}/image_1.png`,
					backgroundColor: colors.mobilePrimaryBlue50,
					title: format("programBuilder.alternativeExercises.infoDialog.page1.title"),
					description: createParagraphBlock(format("programBuilder.alternativeExercises.infoDialog.page1.description")),
					ctaLabel: format("global.dialog.next"),
					shortCtaLabel: format("global.dialog.next"),
				},
				{
					imageUrl: `https://res.cloudinary.com/physio-me/image/upload/v1707823260/Physio.Me/Alternative%20Exercises%20Feature%20Preview/${locale}/image_2.png`,
					backgroundColor: colors.mobilePrimaryBlue15,
					title: format("programBuilder.alternativeExercises.infoDialog.page2.title"),
					description: createParagraphBlock(format("programBuilder.alternativeExercises.infoDialog.page2.description")),
					ctaLabel: format("global.close"),
					shortCtaLabel: format("global.close"),
				},
			],
		}),
		[format, locale],
	);

	return (
		<>
			<Header>
				<AnimatedHalo shouldDisplayHalo={showHalo}>
					<StyledInfoIcon
						width={24}
						height={24}
						color={colors.grey}
						onClick={() => {
							sendAnalyticsOpenAlternativeExercisesInfoTooltip();
							dialogContext?.openModal(ModalId.Announcement, {
								dialogContent: ALTERNATIVE_EXERCISE_FEATURE_PREVIEW,
							});
							setShowHalo(false);
							setShowGuide04Panel(false);
						}}
					/>
				</AnimatedHalo>
				<p className="p-size-m-bold">{format("programBuilder.alternativeExerciseDialog.header")}</p>
				<StyledCrossIcon width={24} height={24} color={colors.grey} onClick={onCrossClicked} />
			</Header>
			{showGuide04Panel && (
				<BottomOnboardingPanel
					text={format("programBuilder.quickstart.step.addAlternativeExercises.guide04")}
					onClose={async () => {
						// Do NOT pass GUIDE_03 on guide04 close anymore; GUIDE_03 is sent when add completes
						setShowHalo(false);
						setShowGuide04Panel(false);
					}}
				/>
			)}
		</>
	);

	// Styled components and panel for onboarding
};

const Header = styled.div`
	${row(0, "spacebetween", "center")};
	height: ${AlternativeExerciseHeaderHeight}px;
	position: sticky;
	top: 0;
	background-color: ${colors.primary10};
	z-index: 30;
`;

const StyledInfoIcon = styled(InfoIcon)`
	cursor: pointer;
`;

const StyledCrossIcon = styled(CrossIcon)`
	cursor: pointer;
`;

const BottomOnboardingPanel = styled(OnboardingPanel)`
	margin-top: 8px;
	margin-bottom: 8px;
`;
